import { Add, PostAdd, Error as ErrorIcon, Visibility, Delete } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Fragment, useCallback, useMemo, useState } from "react";
import LabelWrapper from "../../root/LabelWrapper";
import { ProjectFile } from "../Project";
import { useDropzone } from "react-dropzone";
import dayjs from "dayjs";
import ApiService from "@/api/ApiService";
import { useQuery } from "@tanstack/react-query";

const baseStyle = {
  flex: 1,
  marginTop: "20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
};

const focusedStyle = {
  backgroundColor: "#eaeaea",
};

const acceptStyle = {
  backgroundColor: "rgb(223, 255, 205)",
};

const rejectStyle = {
  backgroundColor: "rgb(255, 205, 205)",
};

type FileCategory = {
  id: number;
  label: string;
  description: string;
  defaultCategory: boolean;
};

export default function AddFileDialog({
  save,
  OpenButton,
  initialState,
}: {
  save: (order: ProjectFile) => void;
  OpenButton: any;
  initialState: ProjectFile | null;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const [open, setOpen] = useState(false);

  const [createdAt, setCreatedAt] = useState(dayjs().utc().format("YYYY-MM-DD HH:mm:ss"));
  const [description, setDescription] = useState("");
  const [filename, setFilename] = useState("");
  const [originalFilenames, setOriginalFilenames] = useState({});
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const { data: categories = [], isLoading: categoriesLoading, isError: categoriesError } = useQuery<FileCategory[]>({
    queryKey: ["fileCategories", "project"],
    queryFn: ApiService.getEntitiesWithFilter("fileCategory", { type: "project" }),
  });
  const [id, setId] = useState(0);
  const [linktype, setLinktype] = useState<"LINK" | "UPLOAD">("UPLOAD");

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const onDrop = async (acceptedFiles) => {
    setIsLoading(true);
    setIsError(false);
    try {
      const response = await ApiService.uploadFile()(acceptedFiles[0]);
      setFilename(response.file);
      setOriginalFilenames((x) => ({ ...x, [response.file]: acceptedFiles[0].name }));
      setIsLoading(false);
    } catch (e) {
      console.error(e);
      setIsError(true);
      setIsLoading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive, isFocused, isDragAccept, isDragReject } = useDropzone({
    disabled: filename.length > 0 || isLoading,
    onDrop,
    accept: { "*": [] },
  });

  const openAndSet = useCallback(() => {
    setDescription(!initialState ? "" : initialState.description);
    setFilename(!initialState ? "" : initialState.filename);
    setOriginalFilenames(!initialState ? {} : initialState.originalFilenames);
    setLinktype(!initialState ? "UPLOAD" : initialState.filename.startsWith("http") ? "LINK" : "UPLOAD");
    setId(!initialState ? 0 : initialState.id);
    setCreatedAt(!initialState ? dayjs().utc().format("YYYY-MM-DD HH:mm:ss") : initialState.createdAt);
    setCategoryId(initialState ? initialState.betterCategory ?? null : categories.find((item) => item.defaultCategory)?.id ?? null);
    setOpen(true);
  }, [initialState, categories]);

  const availableCategories = useMemo(() => {
    if (categoryId !== null && initialState?.betterCategory === categoryId && initialState.category && !categories.some((item) => item.id === categoryId)) {
      return [...categories, { id: categoryId, label: initialState.category, description: "", defaultCategory: false }];
    }
    return categories;
  }, [categories, categoryId, initialState]);

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject],
  );

  return (
    <Fragment>
      <OpenButton onClick={openAndSet} disabled={categoriesLoading} />
      <Dialog open={open} maxWidth="md" onClose={close} fullWidth>
        <Fragment>
          <DialogTitle
            sx={{
              p: 1,
              px: 2,
              background: "#002169",
              color: "white",
              borderBottom: "1px solid rgba(255,255,255,0.5)",
            }}
          >
            Datei/Bild/Link {id > 0 ? "bearbeiten" : "hochladen"}
          </DialogTitle>
          <DialogContent
            sx={{
              p: 0,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <LabelWrapper label="Kategorie">
              <Autocomplete
                options={availableCategories}
                value={availableCategories.find((c) => c.id === categoryId) ?? null}
                getOptionLabel={(option) => option.label}
                renderOption={(props, option) => (
                  <li {...props}>
                    <div>
                      <div>{option.label}</div>
                      {option.description && <div style={{ fontSize: "0.8em", color: "gray" }}>{option.description}</div>}
                    </div>
                  </li>
                )}
                onChange={(_, newValue) => {
                  setCategoryId(newValue?.id ?? null);
                }}
                loading={categoriesLoading}
                renderInput={(params) => <TextField {...params} size="small" placeholder="Kategorie" error={categoriesError} helperText={categoriesError ? "Kategorien konnten nicht geladen werden." : undefined} />}
              />
            </LabelWrapper>
            <LabelWrapper label="Beschreibung">
              <TextField
                size="small"
                fullWidth
                value={description}
                placeholder="Beschreibung"
                onChange={(e) => setDescription((e.target as HTMLInputElement).value)}
              />
            </LabelWrapper>
            <LabelWrapper label="Art">
              <ToggleButtonGroup
                orientation="horizontal"
                exclusive
                value={linktype}
                onChange={(_, type) => {
                  setLinktype(type);
                  setFilename("");
                }}
              >
                <ToggleButton value="UPLOAD">Bild/Dokument</ToggleButton>
                <ToggleButton value="LINK">Link</ToggleButton>
              </ToggleButtonGroup>
            </LabelWrapper>
            {linktype === "UPLOAD" ? (
              <LabelWrapper label="Bild/Dokument hinzufügen">
                <Paper variant="outlined" {...getRootProps({ style })}>
                  <input {...getInputProps()} />
                  {filename.length > 0 ? (
                    <Fragment>
                      Datei erfolgreich hochgeladen.
                      {filename.split(",").map((fn, i, all) => (
                        <Box key="fn" sx={{ display: "flex", mt: "10px", gap: "10px" }}>
                          <Box>
                            Datei {i + 1} von {all.length}
                          </Box>
                          <Button
                            href={(import.meta.env.VITE_APP_URL + "/image/") + fn}
                            target="_blank"
                            startIcon={<Visibility />}
                            variant="outlined"
                          >
                            Ansehen
                          </Button>
                          <Button
                            onClick={() =>
                              setFilename((s) =>
                                s
                                  .split(",")
                                  .filter((f) => f !== fn)
                                  .join(","),
                              )
                            }
                            color="error"
                            startIcon={<Delete />}
                          >
                            Löschen
                          </Button>
                        </Box>
                      ))}
                    </Fragment>
                  ) : isError ? (
                    <Fragment>
                      <ErrorIcon color="error" /> Fehler beim Hochladen. Bitte erneut versuchen.
                    </Fragment>
                  ) : isLoading ? (
                    <CircularProgress color="info" />
                  ) : (
                    <Box sx={{ textAlign: "center", color: "#444" }}>
                      <PostAdd />
                      <Box>Datei hier ablegen</Box>
                    </Box>
                  )}
                </Paper>
              </LabelWrapper>
            ) : (
              <LabelWrapper label={"Link"}>
                <TextField
                  size="small"
                  fullWidth
                  value={filename}
                  helperText={filename.length > 0 && !filename.startsWith("http") ? "Der Link muss mit http beginnen." : undefined}
                  placeholder="Link"
                  onChange={(e) => setFilename((e.target as HTMLInputElement).value)}
                />
              </LabelWrapper>
            )}
          </DialogContent>
          <DialogActions sx={{ borderTop: "1px solid #aaa" }}>
            <Button onClick={close}>Abbrechen</Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={categoriesLoading || categoriesError || categoryId === null || filename.length === 0 || description.length === 0 || (linktype === "LINK" && !filename.startsWith("http"))}
              onClick={() => {
                save({
                  id,
                  deleted: false,
                  description,
                  filename,
                  originalFilenames,
                  createdAt,
                  category: availableCategories.find((item) => item.id === categoryId)?.label ?? "",
                  betterCategory: categoryId,
                });
                close();
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Fragment>
      </Dialog>
    </Fragment>
  );
}
